import { useState, useEffect, useContext } from "react";
import MainStatCard from "../../../components/analytics/MainStatCard";
import ConversionRateCard from "../../../components/analytics/ConversionRateCard";
import SectionAnalytics from "../../../components/analytics/SectionAnalytics";
import TimeSavedGraphCard from "../../../components/analytics/TimeSavedGraphCard";
import LeadTimeHistogramCard from "../../../components/analytics/LeadTimeHistogramCard";
import ResponseTimeMetricsCard from "../../../components/analytics/ResponseTimeMetricsCard";
import ChannelDistributionCard from "../../../components/analytics/ChannelDistributionCard";
import FunnelCard from "../../../components/analytics/FunnelCard";
import HumanResponseTimeCard from "../../../components/analytics/HumanResponseTimeCard";
import EmployeeMetricCard from "../../../components/analytics/EmployeeMetricCard";
import ListingsPerDayChart from "../../../components/analytics/ListingsPerDayChart";
import { ChartSkeleton, StatSkeleton } from "../../../components/analytics/ChartSkeleton";
import { trpc } from "../../../trpc/index";
import { transformDataForRecharts, formatDecimal } from "../../../utils";
import MerchantContext from "../../../contexts/MerchantContext";

type Props = {
  startDate: string;
  endDate: string;
  merchantId: string;
  moveInDateRange?: {
    minDays: number;
    maxDays: number;
  };
};

const AnalyticsAppointmentsContainer = ({
  startDate,
  endDate,
  merchantId,
  moveInDateRange,
}: Props) => {
  const { merchantType } = useContext(MerchantContext);
  const isRentals = merchantType === "rentals";
  const [fetching, setFetching] = useState(true);
  const [leadDirection, setLeadDirection] = useState<
    "all" | "inbound" | "inbound-email" | "outbound" | "pauta"
  >("all");
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [selectedCampaign, setSelectedCampaign] = useState<string>("all");

  const { data: conversionData, refetch: refetchConversion } =
    trpc.analytics.appointmentsConversion.useQuery(
      {
        merchantId,
        startDate,
        endDate,
        daysUntilMoveIn: moveInDateRange
          ? {
              min: moveInDateRange.minDays,
              max: moveInDateRange.maxDays,
            }
          : undefined,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
        onSuccess: () => {
          setFetching(false);
        },
        onError: () => {
          setFetching(false);
        },
      }
    );

  const { data: reachabilityData, refetch: refetchReachability } =
    trpc.analytics.leadsReachability.useQuery(
      {
        merchantId,
        startDate,
        endDate,
        daysUntilMoveIn: moveInDateRange
          ? {
              min: moveInDateRange.minDays,
              max: moveInDateRange.maxDays,
            }
          : undefined,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: outsideHoursData, refetch: refetchOutsideHours } =
    trpc.analytics.leadsOutsideHours.useQuery(
      {
        merchantId,
        startDate,
        endDate,
        daysUntilMoveIn: moveInDateRange
          ? {
              min: moveInDateRange.minDays,
              max: moveInDateRange.maxDays,
            }
          : undefined,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: timeSavedData, refetch: refetchTimeSaved } = trpc.analytics.timeSaved.useQuery(
    {
      merchantId,
      startDate,
      endDate,
      daysUntilMoveIn: moveInDateRange
        ? {
            min: moveInDateRange.minDays,
            max: moveInDateRange.maxDays,
          }
        : undefined,
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
    }
  );

  const { data: leadTimeHistogramData, refetch: refetchLeadTimeHistogram } =
    trpc.analytics.leadTimeHistogram.useQuery(
      {
        merchantId,
        startDate,
        endDate,
        daysUntilMoveIn: moveInDateRange
          ? {
              min: moveInDateRange.minDays,
              max: moveInDateRange.maxDays,
            }
          : undefined,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: responseTimeMetricsData, refetch: refetchResponseTimeMetrics } =
    trpc.analytics.responseTimeMetrics.useQuery(
      {
        merchantId,
        startDate,
        endDate,
        daysUntilMoveIn: moveInDateRange
          ? {
              min: moveInDateRange.minDays,
              max: moveInDateRange.maxDays,
            }
          : undefined,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: humanResponseTimeData, refetch: refetchHumanResponseTime } =
    trpc.analytics.humanResponseTimeMetrics.useQuery(
      {
        merchantId,
        startDate,
        endDate,
        daysUntilMoveIn: moveInDateRange
          ? {
              min: moveInDateRange.minDays,
              max: moveInDateRange.maxDays,
            }
          : undefined,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: leadsByChannelData, refetch: refetchLeadsByChannel } =
    trpc.analytics.leadsByChannel.useQuery(
      {
        merchantId,
        startDate,
        endDate,
        daysUntilMoveIn: moveInDateRange
          ? {
              min: moveInDateRange.minDays,
              max: moveInDateRange.maxDays,
            }
          : undefined,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: transferredLeadsData, refetch: refetchTransferredLeads } =
    trpc.analytics.transferredLeads.useQuery(
      {
        merchantId,
        startDate,
        endDate,
        daysUntilMoveIn: moveInDateRange
          ? {
              min: moveInDateRange.minDays,
              max: moveInDateRange.maxDays,
            }
          : undefined,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: appointmentsByEmployeeData, refetch: refetchAppointmentsByEmployee } =
    trpc.analytics.appointmentsByEmployee.useQuery(
      {
        merchantId,
        startDate,
        endDate,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: transfersByEmployeeData, refetch: refetchTransfersByEmployee } =
    trpc.analytics.transfersByEmployee.useQuery(
      {
        merchantId,
        startDate,
        endDate,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: leadDistributionByEmployeeData, refetch: refetchLeadDistributionByEmployee } =
    trpc.analytics.leadDistributionByEmployee.useQuery(
      {
        merchantId,
        startDate,
        endDate,
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
      }
    );

  const { data: projectsData, refetch: refetchProjects } = trpc.analytics.projects.useQuery(
    {
      merchantId,
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
    }
  );

  const { data: campaignRules } = trpc.inboundCampaignRules.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const {
    data: leadFunnelData,
    refetch: refetchLeadFunnel,
    isFetching: isFetchingLeadFunnel,
  } = trpc.analytics.leadFunnel.useQuery(
    {
      merchantId,
      startDate,
      endDate,
      leadDirection: leadDirection === "all" ? undefined : leadDirection,
      campaignName: selectedCampaign === "all" ? undefined : selectedCampaign,
      projectId: selectedProject === "all" ? undefined : selectedProject,
      daysUntilMoveIn: moveInDateRange
        ? {
            min: moveInDateRange.minDays,
            max: moveInDateRange.maxDays,
          }
        : undefined,
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
    }
  );

  const { data: listingsPerDayData, refetch: refetchListingsPerDay } =
    trpc.analytics.listingsPerDay.useQuery(
      { merchantId, startDate, endDate },
      { enabled: false, refetchOnWindowFocus: false }
    );

  const { data: listingsPerAsesorData, refetch: refetchListingsPerAsesor } =
    trpc.analytics.listingsPerAsesor.useQuery(
      { merchantId, startDate, endDate },
      { enabled: false, refetchOnWindowFocus: false }
    );

  useEffect(() => {
    if (merchantId && startDate && endDate) {
      setFetching(true);

      refetchConversion();
      refetchReachability();
      refetchOutsideHours();
      refetchTimeSaved();
      refetchLeadTimeHistogram();
      refetchResponseTimeMetrics();
      refetchHumanResponseTime();
      refetchLeadsByChannel();
      refetchTransferredLeads();
      refetchAppointmentsByEmployee();
      refetchTransfersByEmployee();
      refetchLeadDistributionByEmployee();
      refetchProjects();
      if (isRentals) refetchListingsPerDay();
      if (isRentals) refetchListingsPerAsesor();
    }
  }, [startDate, endDate, moveInDateRange, merchantId, isRentals]);

  useEffect(() => {
    if (merchantId && startDate && endDate) {
      refetchLeadFunnel();
    }
  }, [leadDirection, selectedProject, selectedCampaign, moveInDateRange, merchantId, startDate, endDate]);

  useEffect(() => {
    if (leadDirection !== "pauta") {
      setSelectedCampaign("all");
    }
  }, [leadDirection]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
  };

  if (fetching) {
    return (
      <div className="flex grow h-full flex-col gap-6">
        <SectionAnalytics title="Alcance" gridClassName="grid-cols-1 md:grid-cols-3">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-1 md:grid-rows-2 md:gap-6">
            <StatSkeleton />
            <StatSkeleton />
          </div>
          <ChartSkeleton colSpan={2} height="h-[220px]" />
        </SectionAnalytics>
        <SectionAnalytics title="Funnel de contactos" gridClassName="grid-cols-1">
          <ChartSkeleton height="h-[400px]" />
        </SectionAnalytics>
        {isRentals && (
          <SectionAnalytics
            title="Leads por inmueble"
            gridClassName="grid-cols-1 md:grid-cols-3"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-1 md:grid-rows-3 md:gap-6">
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
            </div>
            <ChartSkeleton colSpan={2} height="h-[260px]" />
          </SectionAnalytics>
        )}
        {isRentals && (
          <SectionAnalytics
            title="Inmuebles por asesor"
            gridClassName="grid-cols-1 md:grid-cols-2"
          >
            <StatSkeleton />
            <StatSkeleton />
          </SectionAnalytics>
        )}
        <SectionAnalytics
          title="Desempeño"
          gridClassName="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        >
          <ChartSkeleton height="h-[220px]" />
          <ChartSkeleton height="h-[220px]" />
          <ChartSkeleton height="h-[220px]" />
        </SectionAnalytics>
        <SectionAnalytics
          title="Eficiencia"
          gridClassName="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        >
          <ChartSkeleton height="h-[140px]" />
          <ChartSkeleton height="h-[140px]" />
          <ChartSkeleton height="h-[140px]" />
        </SectionAnalytics>
      </div>
    );
  }

  return (
    <div className="flex grow h-full flex-col gap-6">
      <>
          <SectionAnalytics
            title="Alcance"
            gridClassName="grid-cols-1 md:grid-cols-3"
          >
            <div className="grid grid-cols-2 gap-4 md:grid-cols-1 md:grid-rows-2 md:gap-6">
              <MainStatCard
                title="Contactos recibidos"
                value={reachabilityData?.totalLeads || "0"}
              />
              <MainStatCard
                title="Contactos alcanzables"
                value={reachabilityData?.reachableLeads || "0"}
              />
            </div>
            <ConversionRateCard
              title="Tasa de alcance"
              subtitle="Contactos alcanzables"
              label="Contactos alcanzables"
              primaryColor="#4012AB"
              secondaryColor="#DFDEE2"
              colSpan={2}
              data={[
                {
                  label: "numerator",
                  name: "Contactos alcanzables",
                  value: Number(reachabilityData?.reachableLeads || 0),
                },
                {
                  label: "denominator",
                  name: "Contactos no alcanzables",
                  value: Math.max(
                    0,
                    Number(reachabilityData?.totalLeads || 0) -
                      Number(reachabilityData?.reachableLeads || 0)
                  ),
                },
              ]}
              totalDenominator={Number(reachabilityData?.totalLeads || 0)}
              percentage={Number(reachabilityData?.reachabilityRate || 0)}
            />
          </SectionAnalytics>
          <SectionAnalytics title="Funnel de contactos">
            <FunnelCard
              data={(leadFunnelData as any) || null}
              projects={projectsData || []}
              leadDirection={leadDirection}
              selectedProject={selectedProject}
              onDirectionChange={setLeadDirection}
              onProjectChange={handleProjectChange}
              campaignRules={campaignRules}
              selectedCampaign={selectedCampaign}
              onCampaignChange={setSelectedCampaign}
              colSpan={4}
              isLoading={isFetchingLeadFunnel}
            />
          </SectionAnalytics>
          <SectionAnalytics
            title="Desempeño"
            gridClassName="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          >
            <ConversionRateCard
              title="De contactos a agendamiento"
              subtitle="Tasa de conversión"
              label="Citas agendadas"
              primaryColor="#4012AB"
              secondaryColor="#DFDEE2"
              colSpan={1}
              data={[
                {
                  label: "numerator",
                  name: "Citas agendadas",
                  value: Number(conversionData?.totalAppointments || 0),
                },
                {
                  label: "denominator",
                  name: "Contactos alcanzables",
                  value: Number(conversionData?.totalLeads || 0),
                },
              ]}
              percentage={Number(conversionData?.appointmentConversionRate || 0)}
            />
            <ConversionRateCard
              title="De contactos a confirmación"
              subtitle="Tasa de conversión"
              label="Citas confirmadas"
              primaryColor="#4012AB"
              secondaryColor="#DFDEE2"
              colSpan={1}
              data={[
                {
                  label: "numerator",
                  name: "Citas confirmadas",
                  value: Number(conversionData?.totalConfirmed || 0),
                },
                {
                  label: "denominator",
                  name: "Contactos alcanzables",
                  value: Number(conversionData?.totalLeads || 0),
                },
              ]}
              percentage={Number(conversionData?.confirmedConversionRate || 0)}
            />
            <ConversionRateCard
              title="De contactos a cierre"
              subtitle="Tasa de conversión"
              label="Negocios cerrados"
              primaryColor="#4012AB"
              secondaryColor="#DFDEE2"
              colSpan={1}
              data={[
                {
                  label: "numerator",
                  name: "Negocios cerrados",
                  value: Number(conversionData?.totalClosed || 0),
                },
                {
                  label: "denominator",
                  name: "Contactos alcanzables",
                  value: Number(conversionData?.totalLeads || 0),
                },
              ]}
              percentage={Number(conversionData?.closedConversionRate || 0)}
            />
          </SectionAnalytics>
          <SectionAnalytics
            title="Transferencias"
            gridClassName="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          >
            <ConversionRateCard
              title="De contactos a transferencia"
              subtitle="Tasa de conversión"
              label="Contactos transferidos"
              primaryColor="#4012AB"
              secondaryColor="#DFDEE2"
              colSpan={1}
              data={[
                {
                  label: "numerator",
                  name: "Contactos transferidos",
                  value: Number(transferredLeadsData?.transferredLeadsCount || 0),
                },
                {
                  label: "denominator",
                  name: "Contactos alcanzables",
                  value: Number(transferredLeadsData?.totalLeads || 0),
                },
              ]}
              percentage={Number(transferredLeadsData?.transferRate || 0)}
            />
          </SectionAnalytics>
          <SectionAnalytics
            title="Citas y negocios cerrados"
            gridClassName="grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
          >
            <MainStatCard
              title="Total de citas agendadas"
              value={conversionData?.totalAppointments || "0"}
            />
            <MainStatCard
              title="Total de citas confirmadas"
              value={conversionData?.totalConfirmed || "0"}
            />
            <MainStatCard
              title="Total de negocios cerrados"
              value={conversionData?.totalClosed || "0"}
            />
            <MainStatCard
              title="Contactos transferidos"
              value={transferredLeadsData?.transferredLeadsCount || "0"}
            />
          </SectionAnalytics>
          {isRentals && (
            <SectionAnalytics
              title="Leads por inmueble"
              gridClassName="grid-cols-1 md:grid-cols-3"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-1 md:gap-6">
                <MainStatCard
                  title="Inmuebles disponibles"
                  value={(listingsPerDayData?.latestTotalListings ?? 0).toLocaleString("es-CO")}
                />
                {(listingsPerDayData?.latestTotalListingsIncludingMls ?? 0) >
                  (listingsPerDayData?.latestTotalListings ?? 0) && (
                  <MainStatCard
                    title="Inmuebles disponibles (con MLS)"
                    value={listingsPerDayData!.latestTotalListingsIncludingMls.toLocaleString(
                      "es-CO"
                    )}
                  />
                )}
                <MainStatCard
                  title="Leads por inmueble"
                  value={formatDecimal(listingsPerDayData?.leadsPerListing ?? 0, 2)}
                  info={`Total de leads del período / inmuebles disponibles del último snapshot del período. ${(
                    listingsPerDayData?.totalNewLeads ?? 0
                  ).toLocaleString("es-CO")} leads / ${(
                    listingsPerDayData?.latestTotalListings ?? 0
                  ).toLocaleString("es-CO")} inmuebles = ${formatDecimal(
                    listingsPerDayData?.leadsPerListing ?? 0,
                    2
                  )}`}
                />
              </div>
              <ListingsPerDayChart
                data={listingsPerDayData?.rows ?? []}
                colSpan={2}
              />
            </SectionAnalytics>
          )}
          {isRentals && (
            <SectionAnalytics
              title="Inmuebles por asesor"
              gridClassName="grid-cols-1 md:grid-cols-2"
            >
              <MainStatCard
                title="Asesores activos"
                value={(listingsPerAsesorData?.totalActiveAsesores ?? 0).toLocaleString("es-CO")}
                info="Asesores actualmente activos en la plataforma. Excluye asesores inactivos o eliminados. Es un conteo en tiempo real, no del período seleccionado."
              />
              <MainStatCard
                title="Inmuebles por asesor"
                value={formatDecimal(listingsPerAsesorData?.listingsPerAsesor ?? 0, 2)}
                info={`Inmuebles disponibles del último snapshot del período / asesores activos actuales. ${(
                  listingsPerAsesorData?.latestTotalListings ?? 0
                ).toLocaleString("es-CO")} inmuebles / ${(
                  listingsPerAsesorData?.totalActiveAsesores ?? 0
                ).toLocaleString("es-CO")} asesores = ${formatDecimal(
                  listingsPerAsesorData?.listingsPerAsesor ?? 0,
                  2
                )}`}
              />
            </SectionAnalytics>
          )}
          <SectionAnalytics
            title="Desempeño por asesor"
            gridClassName="grid-cols-1 md:grid-cols-2"
          >
            <EmployeeMetricCard
              title="Distribución real por asesor"
              subtitle="Total de leads únicos asignados por asesor (transferencias y citas combinadas)"
              emptyMessage="Sin leads asignados en este período"
              info="Muestra cuántos leads únicos terminó atendiendo cada asesor en el período, sumando los que fueron transferidos y los que tuvieron cita. Un mismo lead asignado a un asesor por ambas vías se cuenta una sola vez."
              data={(leadDistributionByEmployeeData || [])
                .slice()
                .sort((a, b) => b.leads - a.leads)
                .map((row) => ({
                  employeeName: row.employeeName,
                  count: row.leads,
                  percentageOfTotal: row.percentageOfTotal,
                }))}
              colSpan={2}
            />
            <EmployeeMetricCard
              title="Citas por asesor"
              subtitle="Total de leads con cita agendada por asesor en el período seleccionado"
              emptyMessage="Sin citas agendadas en este período"
              info="Cuenta los leads únicos a los que se les agendó al menos una cita en el período, asignados a cada asesor. Un mismo lead con varias citas se cuenta una sola vez."
              data={(appointmentsByEmployeeData || [])
                .slice()
                .sort((a, b) => b.appointments - a.appointments)
                .map((row) => ({
                  employeeName: row.employeeName,
                  count: row.appointments,
                  percentageOfTotal: row.percentageOfTotal,
                }))}
              colSpan={1}
            />
            <EmployeeMetricCard
              title="Transferencias por asesor"
              subtitle="Total de leads transferidos por asesor en el período seleccionado"
              emptyMessage="Sin transferencias en este período"
              info="Cuenta los leads únicos transferidos a cada asesor en el período. Si un lead transferido luego agenda una cita, también aparecerá en 'Citas por asesor' — ambos gráficos son independientes."
              data={(transfersByEmployeeData || [])
                .slice()
                .sort((a, b) => b.transfers - a.transfers)
                .map((row) => ({
                  employeeName: row.employeeName,
                  count: row.transfers,
                  percentageOfTotal: row.percentageOfTotal,
                }))}
              colSpan={1}
            />
          </SectionAnalytics>
          <SectionAnalytics title="Canales de contacto" gridClassName="grid-cols-1 md:grid-cols-1">
            <ChannelDistributionCard data={leadsByChannelData || []} />
          </SectionAnalytics>
          <SectionAnalytics
            title="Horario de atención"
            gridClassName="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          >
            <ConversionRateCard
              title="Clientes atendidos"
              subtitle="Fuera del horario laboral"
              label="Fuera del horario laboral"
              primaryColor="#4012AB"
              secondaryColor="#DFDEE2"
              colSpan={1}
              data={[
                {
                  label: "numerator",
                  name: "Fuera del horario laboral",
                  value: Number(outsideHoursData?.outsideBusinessHoursCount || 0),
                },
                {
                  label: "denominator",
                  name: "Dentro del horario laboral",
                  value: Number(outsideHoursData?.withinBusinessHoursCount || 0),
                },
              ]}
              percentage={
                (Number(outsideHoursData?.outsideBusinessHoursCount || 0) /
                  (Number(outsideHoursData?.outsideBusinessHoursCount || 0) +
                    Number(outsideHoursData?.withinBusinessHoursCount || 0))) *
                100
              }
              totalDenominator={
                Number(outsideHoursData?.outsideBusinessHoursCount || 0) +
                Number(outsideHoursData?.withinBusinessHoursCount || 0)
              }
            />
            <LeadTimeHistogramCard data={leadTimeHistogramData || []} colSpan={2} />
          </SectionAnalytics>
          <SectionAnalytics
            title="Eficiencia"
            gridClassName="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          >
            <ResponseTimeMetricsCard
              avgSeconds={responseTimeMetricsData?.avgSeconds || 0}
              p0={responseTimeMetricsData?.p0 || 0}
              p25={responseTimeMetricsData?.p25 || 0}
              p50={responseTimeMetricsData?.p50 || 0}
              p75={responseTimeMetricsData?.p75 || 0}
              p95={responseTimeMetricsData?.p95 || 0}
            />
            <TimeSavedGraphCard
              hours={
                timeSavedData?.length
                  ? Number(timeSavedData[timeSavedData.length - 1].totalTimeInHoursAcum)
                  : 0
              }
              data={transformDataForRecharts(
                timeSavedData?.reduce(
                  (acc, item) => {
                    acc[item.messageDate] = Number(item.totalTimeInHoursAcum);
                    return acc;
                  },
                  {} as Record<string, number>
                ) || {}
              )}
            />
            <HumanResponseTimeCard employees={humanResponseTimeData?.employees || []} />
          </SectionAnalytics>
        </>
    </div>
  );
};

export default AnalyticsAppointmentsContainer;
